import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useEffect, useState } from "react";

type EditFieldDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  field: "name" | "description";
  currentValue: string | null;
  onSave: (value: string) => Promise<unknown>;
};

export const EditFieldDialog = ({
  open,
  onOpenChange,
  field,
  currentValue,
  onSave,
}: EditFieldDialogProps) => {
  const [value, setValue] = useState(currentValue ?? "");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setValue(currentValue ?? "");
    }
  }, [open, currentValue]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(value);
      onOpenChange(false);
    } catch {
      // Error feedback already shown by onSave — dialog stays open
    } finally {
      setIsSaving(false);
    }
  };

  const label = field === "name" ? "Name" : "Description";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit {label}</DialogTitle>
        </DialogHeader>
        {field === "name" ? (
          <Input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={label}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSave();
            }}
          />
        ) : (
          <Textarea
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={label}
            rows={4}
          />
        )}
        <DialogFooter>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button size="sm" onClick={handleSave} disabled={isSaving}>
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
